import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { semanticTokens, themeFallbacks } from "@packages/config/theme";
import React, { useEffect, useState } from "react";

/**
 * QR Code Component — Simple API, Apple-quality rendering
 *
 * Uses qr-code-styling library for premium QR generation with rounded dots.
 * Falls back to basic qrcode library if qr-code-styling fails.
 *
 * For more advanced customization (logo, variants, shimmer), use AppleQR.
 */
export interface QRProps {
  value: string;
  size?: number;      // px (default: 200)
  margin?: number;    // QR margin in modules (default: 2)
  alt?: string;       // Alt text for accessibility
  className?: string; // Additional CSS classes
}

// Get computed theme colors
const getThemeColors = (): { foreground: string; background: string } => {
  if (typeof window === 'undefined') {
    // SSR fallback: use theme fallbacks from semantic tokens
    return { foreground: themeFallbacks.foreground, background: themeFallbacks.background };
  }

  try {
    const root = getComputedStyle(document.documentElement);
    const fgHsl = root.getPropertyValue('--foreground').trim();
    const bgHsl = root.getPropertyValue('--background').trim();

    if (!fgHsl || !bgHsl) {
      // Fallback: use theme fallbacks from semantic tokens
      return { foreground: themeFallbacks.foreground, background: themeFallbacks.background };
    }

    const hslToHex = (hsl: string): string => {
      try {
        const parts = hsl.split(' ').filter(Boolean);
        if (parts.length < 3) return themeFallbacks.foreground;

        const h = parseFloat(parts[0]) || 0;
        const s = parseFloat(parts[1].replace('%', '')) || 0;
        const l = parseFloat(parts[2].replace('%', '')) || 0;

        const sNorm = s / 100;
        const lNorm = l / 100;

        const c = (1 - Math.abs(2 * lNorm - 1)) * sNorm;
        const x = c * (1 - Math.abs((h / 60) % 2 - 1));
        const m = lNorm - c / 2;

        let r = 0, g = 0, b = 0;

        if (h >= 0 && h < 60) { r = c; g = x; b = 0; }
        else if (h >= 60 && h < 120) { r = x; g = c; b = 0; }
        else if (h >= 120 && h < 180) { r = 0; g = c; b = x; }
        else if (h >= 180 && h < 240) { r = 0; g = x; b = c; }
        else if (h >= 240 && h < 300) { r = x; g = 0; b = c; }
        else { r = c; g = 0; b = x; }

        const toHex = (n: number) => {
          const val = Math.round((n + m) * 255);
          return Math.max(0, Math.min(255, val)).toString(16).padStart(2, '0');
        };
        return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
      } catch {
        return themeFallbacks.foreground;
      }
    };

    return {
      foreground: hslToHex(fgHsl),
      background: hslToHex(bgHsl)
    };
  } catch {
    // Fallback: use theme fallbacks from semantic tokens
    return { foreground: themeFallbacks.foreground, background: themeFallbacks.background };
  }
};

export const QR: React.FC<QRProps> = ({
  value,
  size = 200,
  margin = 2,
  alt = "QR Code",
  className = ""
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const generateQR = async () => {
      if (!value) {
        setIsLoading(false);
        setError('No value provided');
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        setQrDataUrl(null);

        const colors = getThemeColors();

        // Try qr-code-styling first
        try {
          const QRCodeStyling = (await import('qr-code-styling')).default;

          const qrCode = new QRCodeStyling({
            width: size,
            height: size,
            type: 'svg',
            data: value,
            margin: margin,
            qrOptions: {
              errorCorrectionLevel: 'M',
            },
            dotsOptions: {
              color: colors.foreground,
              type: 'rounded',
            },
            cornersSquareOptions: {
              color: colors.foreground,
              type: 'extra-rounded',
            },
            cornersDotOptions: {
              color: colors.foreground,
              type: 'dot',
            },
            backgroundOptions: {
              color: colors.background,
            },
          });

          const blob = await qrCode.getRawData('svg');
          if (blob && !cancelled) {
            const svgText = await blob.text();
            const dataUrl = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgText)))}`;
            setQrDataUrl(dataUrl);
            setIsLoading(false);
            return;
          }
        } catch (stylingError) {
          console.warn('[QR] qr-code-styling failed, falling back:', stylingError);
        }

        // Fallback to basic qrcode library
        const qrcode = await import('qrcode');
        const dataUrl = await qrcode.toDataURL(value, {
          width: size,
          margin: margin,
          color: {
            dark: colors.foreground,
            light: colors.background
          },
          errorCorrectionLevel: 'M'
        });

        if (!cancelled) {
          setQrDataUrl(dataUrl);
          setIsLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[QR] Failed to generate:', err);
          setError(err instanceof Error ? err.message : 'Failed to generate QR code');
          setIsLoading(false);
        }
      }
    };

    generateQR();

    return () => {
      cancelled = true;
    };
  }, [value, size, margin]);

  if (error) {
    return (
      <div
        className={cn(
          "flex items-center justify-center rounded-lg border",
          semanticTokens.bg.muted,
          semanticTokens.border.default,
          className
        )}
        style={{ width: size, height: size }}
        role="img"
        aria-label={`${alt} - Error: ${error}`}
      >
        <span className={cn("text-xs text-center px-2", semanticTokens.fg.muted)}>
          QR code unavailable
        </span>
      </div>
    );
  }

  if (isLoading || !qrDataUrl) {
    return (
      <Skeleton
        className={cn("flex items-center justify-center rounded-lg", className)}
        style={{ width: size, height: size }}
        role="img"
        aria-label={`${alt} - Loading`}
      >
        <span className={cn("text-xs", semanticTokens.fg.muted)}>Generating...</span>
      </Skeleton>
    );
  }

  return (
    <img
      src={qrDataUrl}
      alt={alt}
      width={size}
      height={size}
      className={cn("rounded-lg", className)}
      style={{ imageRendering: 'crisp-edges' }}
    />
  );
};
